
import { Integer } from '@type-r/ext-types';
import { attributes, AttributesMixin, define, mixins, Model, type, Collection } from '@type-r/models';
import { createdUpdatedAttrs, matchString } from 'api/common';
import { fetchJson, restfulIO } from "api/endpoint";
import { root } from './common';
import { ConversationMixin } from './conversation';
import { DateRangeFilter, TextFilter, MultiSelectFilter, asCurrency } from 'lqx-react';
import { CompanyModel, PlatformCompany } from 'api/relationship';
import { globalStore } from 'api';
import { Dictionary } from 'lodash';
import { filterByFiscalPeriod, filterByFiscalYear, FiscalCalendar, FiscalCalendarDto } from './fiscal-calendar';

// TODO: this is lowercase, but api returns PascalCase. Figure out how to fix.
export type TransactionCurrentType = 'ar' | 'nonar' | 'uac';

export enum TransactionStatusEnum {
    Reconciled = 'Reconciled',
    Unreconciled = 'Unreconciled',
    PartiallyReconciled = 'Partially-Reconciled',
    NonAr = 'NonAR',
    OneSided = 'One-sided'
};

@define
export class BankTransactionsSummary extends
    attributes({
        allCount : Integer,
        arCount : Integer,
        nonArCount : Integer,
        uacCount : Integer,
        unresolvedCount : Integer
    })
{
    static endpoint = restfulIO( '/api/v2/recs/summary/transactions' )
}

export async function movePaymentTo(moveTo: TransactionCurrentType, transactionId: number, file: any, comment: string): Promise<number> {
    const formData = new FormData();
    if (file) {
        formData.append("file", file, file.name);
    }
    formData.append("comment", comment);

    const url = root + `transactions/${transactionId}/moveto${moveTo}`;

    return await fetchJson('PUT', url, undefined, formData);
}


export interface Transaction extends AttributesMixin<typeof Transaction> {}

@define
export class Transaction extends Model {
    static endpoint = restfulIO( root + 'transactions' );

    static attributes = {
        ...createdUpdatedAttrs,
        conversationId : Integer,
        currency: type(String).value('USD'),
        statementDate: Date,
        amount : Number,
        unreconciledAmount : Number,
        valueDate: Date,
        region: String,
        baiCode : String,
        currentType : String,
        metadata: null,
        customerId : Integer,
        supplierId : Integer,
        typeId : Integer,
        paymentDetail : {
            paymentId : Number,
            creditorName : String,
            creditorBankName : String,
            creditorAccount : String,
            debtorName : String,
            debtorBankName: String,
            debtorAccount : String,
            exchangeRate : String,
            id : String,
            remittanceInfoUnstructured : String,
            entryReferenceNumber : String,
            accountServicerReference : String,
            source: String,
            endToEndId : String,
            additionalEntryInfo : String,
            isDuplicate : Boolean
        },
        payorAgentId : Integer,
        supplierPlatformCompanyId: Number,
        customerPlatformCompanyId: Number,
        payorAgentPlatformCompanyId: Number
    }

    get customer() : PlatformCompany {
        return globalStore.platformCompanies.get(this.customerPlatformCompanyId as any);
    }

    get supplier() : PlatformCompany {
        return globalStore.platformCompanies.get(this.supplierPlatformCompanyId as any);
    }
    
    get customerName() : string {
        return this.customer ? this.customer.name : `Customer #${this.customerId}`;
    }

    get supplierName() : string {
        return this.supplier ? this.supplier.name : `Supplier #${this.supplierId}`;
    }

    get payorAgent() : PlatformCompany {
        return globalStore.platformCompanies.get(this.payorAgentPlatformCompanyId as any);
    }

    get status(): TransactionStatusEnum {
        if (this.isOneSided) {
            return TransactionStatusEnum.OneSided;
        }

        if (this.currentType === 'NonAr') {
            return TransactionStatusEnum.NonAr;
        }

        switch (this.unreconciledAmount) {
            case 0:
                return TransactionStatusEnum.Reconciled;
            case this.amount:
                return TransactionStatusEnum.Unreconciled;
            default:
                return TransactionStatusEnum.PartiallyReconciled;
        }
    }

    get isOneSided(): boolean {
        return this.conversationId && (!this.supplierId || !this.customerId);
    }

    filter( filter : BankTransactionFilters, fiscalCalendarMap: Dictionary<FiscalCalendarDto[]>) : boolean {
        return (!filter.filterAll
            || matchString( this.supplierName, filter.filterAll )
            || matchString( this.customerName, filter.filterAll )
            || matchString( this.region, filter.filterAll )
            || matchString( this.amount.toString(), filter.filterAll )
            || matchString( this.id.toString(), filter.filterAll )
        ) && (!filter.valueDate.from || !filter.valueDate.to || (this.valueDate >= filter.valueDate.from && this.statementDate <= filter.valueDate.to))
        && (!filter.outstandingAmount.value || matchString(asCurrency(this.unreconciledAmount), filter.outstandingAmount.value) || matchString(String(this.unreconciledAmount), filter.outstandingAmount.value))
        && (!filter.amount.value || matchString(asCurrency(this.amount), filter.amount.value) || matchString(String(this.amount), filter.amount.value))
        && filterByMultiSelect(filter.supplierName, this.supplierName)
        && filterByMultiSelect(filter.customerName, this.customerName);
    }

    get debtorBankName() {
        return this.paymentDetail ? this.paymentDetail.debtorBankName : '';
    }

    get debtorAccount() {
        return this.paymentDetail ? this.paymentDetail.debtorAccount : '';
    }

    get creditorAccount() {
        return this.paymentDetail ? this.paymentDetail.creditorAccount : '';
    }

    get creditorBankName() {
        return this.paymentDetail ? this.paymentDetail.creditorBankName : '';
    }

    get entryReferenceNumber() {
        return this.paymentDetail ? this.paymentDetail.entryReferenceNumber : '';
    }

    get remittanceInfoUnstructured() {
        return this.paymentDetail ? this.paymentDetail.remittanceInfoUnstructured : '';
    }

    get additionalEntryInfo() {
        return this.paymentDetail ? this.paymentDetail.additionalEntryInfo : '';
    }
}

export interface BankTransactionFilters extends AttributesMixin<typeof BankTransactionFilters> {}
@define
export class BankTransactionFilters extends Model {
    static attributes = {
        filterAll : '',
        valueDate : DateRangeFilter,
        amount : TextFilter,
        outstandingAmount : TextFilter,
        supplierName: MultiSelectFilter,
        customerName: MultiSelectFilter,
    }

    initializeOptions(fiscalCalendars: Collection<FiscalCalendar>) {
        const companyNames = globalStore.platformCompanies.map(cmp => cmp.name);
        
        this.filterAll = '';
        this.valueDate.set({from: undefined, to: undefined});
        this.amount.set({value: undefined});
        this.outstandingAmount.set({value: undefined});

        this.supplierName.set({selected: undefined, options: Array.from(new Set(companyNames.filter(x => x)))});
        this.customerName.set({selected: undefined, options: Array.from(new Set(companyNames.filter(x => x)))});
    }
}

export async function fetchTransactionByType(type, includePaymentDetals = false) : Promise<Transaction[]> {
    const url = `${root}transactions/${type}?includePaymentDetals=${includePaymentDetals}`;
    return await fetchJson('GET', url);
}

const filterByMultiSelect =  (multiSelect : MultiSelectFilter, value) : boolean => {
    return !multiSelect || !multiSelect.selected || !multiSelect.selected.length
        || multiSelect.selected.includes(value);
}
